import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Printer, Droplet, AlertTriangle, Shield, Zap, Activity,
  ClipboardCheck, FileText, Beaker, Check, Copy, Phone,
} from 'lucide-react';
import Header from '../components/Header';

const INDIGO = '#4338CA';
const BLUE = '#0284C7';
const AMBER = '#D97706';
const RED = '#DC2626';
const GREEN = '#16A34A';

// --- Data ------------------------------------------------------------

const IV_TIERS = [
  { egfr: '> 45', tone: GREEN, label: 'Lowest risk', action: 'Proceed — no special renal precautions routinely required. Risk of IV contrast-related AKI is likely non-existent.' },
  { egfr: '30 - 45', tone: AMBER, label: 'Low risk (stable)', action: 'Proceed in most stable patients. Routine universal hydration NOT recommended. Consider additional precautions if renal function is acutely deteriorating or the patient is clinically unwell.' },
  { egfr: '< 30', tone: RED, label: 'Radiologist review required', action: 'Proceed only after documented risk-benefit assessment and appropriate mitigation where indicated.' },
];

const METFORMIN_IV = [
  { egfr: '> 30', tone: GREEN, rule: 'Continue metformin.' },
  { egfr: '< 30, unknown recent eGFR, or unstable renal function', tone: RED, rule: 'Withhold metformin for at least 48 hours. Recheck eGFR before restarting.' },
];

const METFORMIN_IA = [
  { egfr: '> 45', tone: GREEN, rule: 'Metformin may continue.' },
  { egfr: '< 45, unknown, or unstable/unwell patient', tone: RED, rule: 'Hold metformin for 48 hours. Reassess renal function before restarting.' },
];

const ALLERGY_PATHS = [
  { tone: GREEN, label: 'No prior reaction + no risk factors', action: 'Proceed per standard protocol.' },
  { tone: AMBER, label: 'Previous MILD reaction (urticaria / mild itch / limited nausea)', action: 'Radiologist review. Premedication may be considered at the radiologist’s discretion.' },
  { tone: RED, label: 'Previous MODERATE / SEVERE reaction OR severe systemic allergy', action: 'Radiologist review BEFORE contrast. Consider alternative agent, non-contrast modality, or formal premedication regimen as directed. Document the plan.' },
  { tone: RED, label: 'Known prior ANAPHYLAXIS to same contrast class', action: 'Strongly consider contraindication. Radiologist must document the decision and rationale if proceeding.' },
];

const REACTIONS = [
  {
    tone: AMBER,
    label: 'MILD',
    signs: 'Limited urticaria · mild itch · flushing · mild nausea',
    steps: [
      'Observe; reassure the patient.',
      'Slow or stop the infusion temporarily.',
      'Inform the radiologist.',
      'Oral antihistamine may be considered at the radiologist’s direction.',
      'Extended observation — minimum 30 min before discharge.',
    ],
  },
  {
    tone: '#EA580C',
    label: 'MODERATE',
    signs: 'Widespread urticaria · bronchospasm · facial/laryngeal oedema · persistent vomiting · fluid-responsive hypotension',
    steps: [
      'STOP the infusion immediately.',
      'Call the radiologist urgently.',
      'Oxygen + IV access + IV fluids as directed.',
      'Bronchodilator (e.g., inhaled salbutamol) for bronchospasm as directed.',
      'Consider IM adrenaline per the anaphylaxis SOP if deteriorating.',
      'Prepare for ambulance transfer. Prolonged monitoring post-recovery.',
    ],
  },
  {
    tone: RED,
    label: 'SEVERE / ANAPHYLAXIS',
    signs: 'Severe hypotension · airway compromise · LOC · seizures · cardiac arrest',
    steps: [
      'ACTIVATE THE ANAPHYLAXIS SOP IMMEDIATELY.',
      'IM adrenaline without delay per the displayed protocol.',
      'Call 000 immediately. Prepare for ambulance transfer.',
      'Oxygen, IV fluids, airway management.',
      'Advanced life support as indicated.',
      'Document every intervention with timestamps.',
    ],
  },
];

const SCREENING_QUESTIONS = [
  'Previous contrast reaction (iodinated or gadolinium)?',
  'Severe allergy / anaphylaxis history to other agents?',
  'Asthma — controlled or uncontrolled?',
  'Known thyroid dysfunction (iodinated only)?',
  'Multiple myeloma, diabetic nephropathy, or nephrotoxic medications?',
];

const EMERGENCY_PATHWAY = [
  'Clinical indication confirmed by referring practitioner AND radiologist.',
  'Apply RANZCR emergency principle — do NOT delay imaging solely to obtain an eGFR if delay would compromise care.',
  'Capture whatever renal history IS available (recent pathology, last known eGFR, metformin use).',
  'Radiologist documents the risk-benefit decision in the report.',
  'Post-procedure: arrange a follow-up eGFR and give metformin hold instructions in writing if eGFR was unknown.',
];

const RIS_NOTE = 'Contrast administration per SP Contrast SOP v1.0: Agent [NAME] [DOSE mL], IV, right antecubital. Pre-procedure eGFR [VALUE] ([DATE]). Allergy screening: negative. Metformin: [continued/held]. No immediate reaction. Observed [MIN] minutes post-injection.';

// --- Page ------------------------------------------------------------

export default function ContrastSOPPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(RIS_NOTE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .print-container { padding: 0 !important; max-width: none !important; }
          h1, h2, h3 { color: #0E2D5C !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 print-container" data-testid="contrast-sop-page">
        {/* Top bar */}
        <div className="no-print flex items-center justify-between mb-6">
          <button onClick={() => navigate('/sops')} data-testid="cs-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to SOPs
          </button>
          <button onClick={() => window.print()} data-testid="cs-print" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white" style={{ backgroundColor: INDIGO }}>
            <Printer className="w-3.5 h-3.5" /> Print SOP
          </button>
        </div>

        {/* Cover */}
        <div className="rounded-2xl border p-6 sm:p-8 mb-6" style={{ borderColor: `${INDIGO}33`, background: `linear-gradient(135deg, ${INDIGO}0F, transparent 60%)` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${INDIGO}15`, color: INDIGO }}>
              <Droplet className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${INDIGO}1A`, color: INDIGO }}>
              SOP · v1.0 · RANZCR-aligned
            </span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Contrast Administration — Iodinated &amp; Gadolinium</h1>
          <p className="text-sm text-muted-foreground mt-1">Renal thresholds · metformin rules · allergy framework · emergency pathway · reaction escalation</p>
          <div className="mt-5 flex flex-wrap gap-5 text-xs">
            <Meta label="Owner" value="Medical Director" />
            <Meta label="Governance" value="Practice Manager" />
            <Meta label="References" value="RANZCR Iodinated V2.3 (Mar 2018) · RANZCR Gadolinium Guidance" />
          </div>
        </div>

        {/* Core principle */}
        <div className="rounded-2xl border p-5 mb-6" style={{ borderColor: `${INDIGO}55`, backgroundColor: `${INDIGO}0A` }} data-testid="cs-core-principle">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 mt-0.5 shrink-0" style={{ color: INDIGO }} />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: INDIGO }}>Core Principle</p>
              <p className="text-base font-semibold text-foreground mt-1">
                All contrast administration is governed by a documented risk-benefit assessment. Emergency imaging must <span className="underline decoration-2" style={{ textDecorationColor: RED }}>not</span> be delayed solely to obtain renal function results where delay would compromise patient care.
              </p>
            </div>
          </div>
        </div>

        {/* 1. IV iodinated thresholds */}
        <SectionHeader n="1" title="IV Iodinated Contrast — Renal Thresholds" tone={INDIGO} />
        <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="cs-iv-tiers">
          {IV_TIERS.map((t, i) => (
            <TierCard key={i} tone={t.tone} egfr={t.egfr} label={t.label} action={t.action} />
          ))}
        </div>
        <QuotePill text="Patients with stable eGFR ≥30 mL/min/1.73 m² can usually proceed with intravenous iodinated contrast, with documented radiologist review required for eGFR <30 or unstable renal function." />

        {/* 2. Metformin IV */}
        <SectionHeader n="2" title="Metformin — IV Iodinated Contrast" tone={INDIGO} />
        <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="cs-metformin-iv">
          {METFORMIN_IV.map((r, i) => (
            <RuleCard key={i} tone={r.tone} label={`eGFR ${r.egfr}`} rule={r.rule} />
          ))}
        </div>

        {/* 3. Metformin Intra-arterial */}
        <SectionHeader n="3" title="Metformin — Intra-arterial / Angiography (higher threshold)" tone={INDIGO} />
        <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="cs-metformin-ia">
          {METFORMIN_IA.map((r, i) => (
            <RuleCard key={i} tone={r.tone} label={`eGFR ${r.egfr}`} rule={r.rule} />
          ))}
        </div>

        {/* 4. Gadolinium */}
        <SectionHeader n="4" title="Gadolinium-based Contrast (MRI)" tone={INDIGO} />
        <div className="rounded-2xl border p-5 mb-8 bg-card">
          <div className="flex items-start gap-3">
            <Beaker className="w-5 h-5 mt-0.5 shrink-0" style={{ color: BLUE }} />
            <div>
              <p className="text-sm text-foreground"><strong>Significant renal impairment = eGFR &lt;30 mL/min/1.73 m².</strong> Patients with known or suspected renal impairment must be assessed in accordance with current RANZCR guidance before gadolinium administration. <span className="font-semibold">Radiologist review required.</span></p>
            </div>
          </div>
        </div>

        {/* 5. Allergy screening */}
        <SectionHeader n="5" title="Allergy Screening & Premedication Framework" tone={INDIGO} />

        {/* Screening checklist */}
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5" /> Pre-procedure screening — ask every patient</p>
          <ul className="space-y-2 text-sm text-foreground">
            {SCREENING_QUESTIONS.map((q, i) => (
              <li key={i} className="flex items-start gap-2"><span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" />{q}</li>
            ))}
          </ul>
          <p className="text-[11px] text-muted-foreground mt-3 italic">Re-confirm answers at the pre-procedure time-out.</p>
        </div>

        {/* Management by history */}
        <div className="space-y-2 mb-8" data-testid="cs-allergy-paths">
          {ALLERGY_PATHS.map((p, i) => (
            <div key={i} className="flex gap-3 rounded-xl border p-4" style={{ borderColor: `${p.tone}33`, backgroundColor: `${p.tone}08` }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${p.tone}20`, color: p.tone }}>
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{p.label}</p>
                <p className="text-xs text-foreground/85 mt-0.5">{p.action}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-8 text-xs text-foreground">
          <strong>Note on premedication:</strong> specific regimens are NOT pre-defined in this SOP. The radiologist specifies drug, dose and timing on a case-by-case basis in line with current Australian practice (commonly corticosteroid cover + antihistamine pre-dosing).
        </div>

        {/* 6. Emergency pathway */}
        <SectionHeader n="6" title="After-hours / Urgent Contrast Pathway" tone={INDIGO} />
        <div className="space-y-2 mb-8" data-testid="cs-emergency">
          {EMERGENCY_PATHWAY.map((step, i) => (
            <div key={i} className="flex gap-3 rounded-xl border border-border p-4">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold" style={{ backgroundColor: `${INDIGO}15`, color: INDIGO }}>{i + 1}</div>
              <p className="text-sm text-foreground flex-1 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>

        {/* 7. Reaction escalation chart */}
        <SectionHeader n="7" title="Contrast Reaction — Escalation Chart" tone={RED} />
        <div className="space-y-3 mb-8" data-testid="cs-reactions">
          {REACTIONS.map((r, i) => (
            <div key={i} className="rounded-2xl border overflow-hidden bg-card" style={{ borderColor: `${r.tone}40` }}>
              <div className="px-4 py-3 border-b flex items-center gap-3" style={{ backgroundColor: `${r.tone}0A`, borderColor: `${r.tone}30` }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: `${r.tone}20`, color: r.tone }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold uppercase tracking-wider" style={{ color: r.tone }}>{r.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{r.signs}</p>
                </div>
                {r.label.includes('SEVERE') && <Phone className="w-4 h-4 text-red-600 shrink-0" />}
              </div>
              <ol className="px-4 py-3 space-y-1.5 text-sm text-foreground list-decimal pl-10">
                {r.steps.map((s, j) => <li key={j} className={s.includes('IMMEDIATELY') || s.includes('000') || s.includes('STOP') ? 'font-semibold text-red-600' : ''}>{s}</li>)}
              </ol>
            </div>
          ))}
        </div>

        {/* 8. RIS note template */}
        <SectionHeader n="8" title="RIS Note Template" tone={INDIGO} />
        <div className="rounded-xl border border-border bg-card p-5 mb-8">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Copy-paste for routine contrast studies</p>
            <button
              onClick={copy}
              data-testid="cs-copy-ris"
              className="no-print inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border"
              style={copied
                ? { borderColor: GREEN, color: GREEN, backgroundColor: `${GREEN}10` }
                : { borderColor: `${INDIGO}40`, color: INDIGO }}
            >
              {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
          </div>
          <p className="text-sm text-foreground leading-relaxed font-mono bg-muted/60 rounded-md p-3">{RIS_NOTE}</p>
        </div>

        {/* 9. Quarterly audit */}
        <div className="rounded-xl border border-border bg-card p-5 mb-8">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Quarterly audit metrics</p>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm text-foreground">
            {[
              'Proportion of contrast studies with documented eGFR',
              'Proportion where radiologist review was triggered per SOP',
              'Time to reaction management',
              'Post-event review of every moderate/severe reaction',
            ].map((m, i) => (
              <li key={i} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: INDIGO }} /><span>{m}</span></li>
            ))}
          </ul>
        </div>

        <p className="no-print text-center text-[11px] text-muted-foreground mb-2">
          Owner: Medical Director · Governance: Practice Manager · References: RANZCR Iodinated V2.3 (Mar 2018) + RANZCR Gadolinium · Review: Annual
        </p>
      </div>
    </div>
  );
}

// --- Sub-components --------------------------------------------------

function Meta({ label, value }) {
  return (
    <div>
      <span className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground">{label}</span>
      <div className="text-foreground font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function SectionHeader({ n, title, tone }) {
  return (
    <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${tone}15`, color: tone }}>{n}</span>
      {title}
    </h2>
  );
}

function TierCard({ tone, egfr, label, action }) {
  return (
    <div className="rounded-2xl border bg-card overflow-hidden" style={{ borderColor: `${tone}40` }}>
      <div className="px-4 py-3" style={{ backgroundColor: `${tone}0A`, borderBottom: `1px solid ${tone}30` }}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">eGFR (mL/min/1.73 m²)</p>
        <p className="font-['Outfit'] text-2xl font-bold mt-0.5" style={{ color: tone }}>{egfr}</p>
        <p className="text-[11px] font-semibold mt-1" style={{ color: tone }}>{label}</p>
      </div>
      <p className="px-4 py-3 text-xs text-foreground/85 leading-relaxed">{action}</p>
    </div>
  );
}

function RuleCard({ tone, label, rule }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: `${tone}33`, backgroundColor: `${tone}0A` }}>
      <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: tone }}>{label}</p>
      <p className="text-sm text-foreground leading-relaxed">{rule}</p>
    </div>
  );
}

function QuotePill({ text }) {
  return (
    <blockquote className="rounded-xl border-l-4 px-4 py-3 mb-8 italic text-sm text-foreground bg-muted/40" style={{ borderColor: INDIGO }}>
      {text}
    </blockquote>
  );
}
